import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type { AuthorityUiMetadata } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { SearchExperimentLayer } from '@atlassian/search-experiment/dynamic/types';

import { filterCommonAttributes } from './analytics-helpers';
import type { commonAttributesType } from './types';

/**
 * Derives non-UGC authority attributes from the authoritative-answer metadata so
 * they can be attached to the existing aiAnswerResult shown event. Only counts,
 * booleans, and the coarse authority level/confidence are emitted — never source
 * titles, expert names, URLs, or reasoning text.
 */
const getAuthorityAttributes = (authorityUiMetadata?: AuthorityUiMetadata | null) => {
	if (!authorityUiMetadata) {
		return { hasAuthoritativeSources: false };
	}
	return {
		hasAuthoritativeSources: true,
		authorityLevel: authorityUiMetadata.authorityLevel,
		isHighlyAuthoritative:
			authorityUiMetadata.isHighlyAuthoritative ?? authorityUiMetadata.highlyAuthoritative ?? false,
		overallConfidence: authorityUiMetadata.overallConfidence,
		authoritativeSourceCount: authorityUiMetadata.sources?.length ?? 0,
		authoritativeExpertCount: authorityUiMetadata.domainExperts?.length ?? 0,
	};
};

// When the ai answer result is successfully displayed to the user
export const onAIAnswerResultShown: ({
	isStreamed,
	loadTime,
	fmpLoadTime,
	experimentLayers,
	blueLinkCount,
	authorityUiMetadata,
	...props
}: commonAttributesType & {
	isStreamed: boolean;
	loadTime?: number;
	fmpLoadTime?: number;
	experimentLayers?: SearchExperimentLayer[];
	blueLinkCount?: number;
	authorityUiMetadata?: AuthorityUiMetadata | null;
}) => AnalyticsEventPayload = ({
	isStreamed,
	loadTime,
	fmpLoadTime,
	experimentLayers,
	blueLinkCount,
	authorityUiMetadata,
	...props
}: commonAttributesType & {
	isStreamed: boolean;
	loadTime?: number;
	fmpLoadTime?: number;
	experimentLayers?: SearchExperimentLayer[];
	blueLinkCount?: number;
	authorityUiMetadata?: AuthorityUiMetadata | null;
}): AnalyticsEventPayload => {
	const { query, answerString, source, cloudId } = props;
	return {
		action: 'shown',
		actionSubject: 'aiAnswerResult',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			cloudId,
			isStreamed,
			loadTime,
			fmpLoadTime,
			blueLinkCount,
			...(experimentLayers && { backendExperiments: { experimentLayers } }),
			...getAuthorityAttributes(authorityUiMetadata),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};
